import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ErrorToast from "components/Toaster/ErrorToast";
import api from "../api";
import BatchCard from "./BatchCard";

// MenteeCard Component to display individual mentee details
const MenteeCard = ({ mentee, onToggleComplete }) => {
  const {
    id,
    first_name,
    last_name,
    country,
    state,
    name,
    description,
    timestamp,
    progress,
    is_completed = false
  } = mentee;
  const navigate = useNavigate();

  const fullName = `${first_name} ${last_name}`.trim();
  const location =  `${state}, ${country}`.trim();
  const startDate = new Date(timestamp).toLocaleDateString();

  const handleViewButtonClick = () => {
    navigate(`/dashboard/workspace/${id}`);
  };

  const handleToggleComplete = (e) => {
    e.stopPropagation();
    if (onToggleComplete) {
      onToggleComplete(id, !is_completed);
    }
  };

  return (
    <Grid item xs={12} md={6} lg={4}>
      <Card
        sx={{
          borderRadius: "10px",
          border: "1px solid #E8E8E8",
          height: "100%",
          boxShadow: "none",
          p: 2,
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {mentee?.avatar ? (
                <Avatar
                  sx={{ width: 40, height: 40 }}
                  src={mentee?.avatar}
                  alt={fullName}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: "#0172A6",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {fullName?.charAt(0)?.toUpperCase()}
                </Avatar>
              )}
              <Box sx={{ ml: 2 }}>
                <Typography
                  sx={{
                    color: "#202124",
                    fontSize: "16px",
                    fontWeight: 500,
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {fullName}
                </Typography>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: "14px",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {location}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Box onClick={handleToggleComplete} sx={{ cursor: 'pointer' }}>
                {is_completed ? (
                  <CheckCircleOutlineIcon sx={{ color: '#22c55e' }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ color: '#6b7280' }} />
                )}
              </Box>
              <Typography
                sx={{
                  color: "#6b7280",
                  fontSize: "14px",
                  textAlign: "right",
                  fontFamily: "Be Vietnam Pro",
                }}
              >
                Started
              </Typography>
              <Typography
                sx={{
                  color: "#6b7280",
                  fontSize: "14px",
                  fontFamily: "Be Vietnam Pro",
                }}
              >
                {startDate}
              </Typography>
            </Box>
          </Box>

          <Typography
            sx={{
              color: "#202124",
              fontSize: "18px",
              fontWeight: 500,
              mb: 2,
              fontFamily: "Be Vietnam Pro",
            }}
          >
            {name}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 4,
                backgroundColor: "#E8E8E8",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "#0172A6",
                },
              }}
            />
            <Typography
              sx={{
                color: "#6b7280",
                fontSize: "14px",
                minWidth: "70px",
                fontFamily: "Be Vietnam Pro",
              }}
            >
              {`${progress?.toFixed(0)}%`}
            </Typography>
          </Box>

          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#0172A6",
              color: "white",
              textTransform: "capitalize",
              borderRadius: "6px",
              fontFamily: "Be Vietnam Pro",
              fontSize: "14px",
              mt: 1,
              "&:hover": {
                backgroundColor: "#0172A6",
              },
            }}
            onClick={handleViewButtonClick}
          >
            View
          </Button>
        </CardContent>
      </Card>
    </Grid>
  );
};

// MenteeCardList Component to fetch and display list of mentees
const MenteeCardList = () => {
  const [menteeData, setMenteeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const accessToken = localStorage.getItem("access_token");
  const userProfile = useSelector((state) => state.userProfile);
  const userRole = userProfile?.data?.role;
  const [filter, setFilter] = useState('ongoing'); // 'all', 'ongoing', or 'completed'
  const [batchTypeFilter, setBatchTypeFilter] = useState('all'); // 'all', '1:1', 'cohort'
  const navigate = useNavigate();

  // Create cohort modal state
  const [createCohortOpen, setCreateCohortOpen] = useState(false);
  const [newCohortName, setNewCohortName] = useState("");
  const [creatingCohort, setCreatingCohort] = useState(false);

  const handleCreateCohort = async () => {
    if (!newCohortName.trim()) {
      setToastMessage("Please enter a cohort name");
      return;
    }

    setCreatingCohort(true);
    try {
      const response = await api.post("batch/create-cohort/", {
        name: newCohortName.trim(),
      });

      setToastMessage(`Cohort "${response.data.name}" created successfully`);
      setCreateCohortOpen(false);
      setNewCohortName("");

      // Navigate to the new cohort workspace
      navigate(`/dashboard/batch/${response.data.id}/workspace`);
    } catch (err) {
      setToastMessage(err.response?.data?.error || "Failed to create cohort");
    } finally {
      setCreatingCohort(false);
    }
  };


  const handleToggleComplete = async (menteeId, isCompleted) => {
    try {
      const accessToken = localStorage.getItem("access_token");
      await api.post(
        `dashboard/${menteeId}/`,
        { is_completed: isCompleted },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      // Refetch data to update the view
      fetchMenteeData();

      setToastMessage(`Course marked as ${isCompleted ? 'completed' : 'ongoing'}`);
    } catch (err) {
      setToastMessage("Failed to update course status");
      console.error("Error updating completion status:", err);
    }
  };


  const fetchMenteeData = async () => {
    try {
      // Always use batch grouping
      const response = await api.get('dashboard/?group_by=batch');
      setMenteeData(response?.data);
      localStorage.setItem("menteeData", JSON.stringify(response?.data));
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        setToastMessage("Your session has expired. Please log in again.");
      } else {
        setToastMessage("Failed to fetch mentee data");
      }
      setError("Failed to fetch mentee data");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole === "mentor") {
      setLoading(true);
      fetchMenteeData();
    }
  }, [accessToken]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const menteeArray = Array.isArray(menteeData) ? menteeData : [menteeData];

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: "Be Vietnam Pro",
        }}
      >
        <Typography>{error}</Typography>
      </Box>
    );
  }

  if (!menteeData || menteeData?.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100px",
          fontFamily: "Be Vietnam Pro",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Be Vietnam Pro",
            textAlign: "center",
            marginTop: "20px",
          }}
        >
          No Data Available
        </Typography>
      </Box>
    );
  }

  const filteredData = menteeData ?
    (Array.isArray(menteeData) ? menteeData : [menteeData]).filter(item => {
      // Batch type filter
      if (batchTypeFilter !== 'all') {
        if (item.type === 'batch') {
          if (batchTypeFilter === '1:1' && item.batch_type !== 'individual') return false;
          if (batchTypeFilter === 'cohort' && item.batch_type !== 'cohort') return false;
        } else {
          // Non-batch items (orphan courses) only show in 'all' or '1:1' filter
          if (batchTypeFilter === 'cohort') return false;
        }
      }

      // Status filter
      if (filter === 'all') return true;

      // Handle batch items
      if (item.type === 'batch') {
        if (filter === 'ongoing') {
          // Empty batches (no students) count as ongoing
          if (item.member_count === 0) return true;
          return item.completed_count < item.member_count;
        }
        if (filter === 'completed') {
          // Empty batches are not completed
          if (item.member_count === 0) return false;
          return item.completed_count === item.member_count;
        }
      }

      // Handle individual course items
      if (filter === 'ongoing') return !item.is_completed;
      if (filter === 'completed') return item.is_completed;
      return true;
    }) : [];

  return (
    <Box sx={{ p: 3 }}>
      {toastMessage && <ErrorToast message={toastMessage} />}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontFamily: "Be Vietnam Pro",
            }}
          >
            {batchTypeFilter === 'cohort' ? 'My Cohorts' :
             batchTypeFilter === '1:1' ? 'My 1:1 Students' : 'My Students'}
          </Typography>
          {batchTypeFilter === 'cohort' && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCreateCohortOpen(true)}
              sx={{
                backgroundColor: "#0172A6",
                textTransform: "none",
                fontFamily: "Be Vietnam Pro",
                fontSize: "13px",
                "&:hover": {
                  backgroundColor: "#015a84",
                },
              }}
            >
              Create Cohort
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Batch type filter */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              sx={{
                backgroundColor: batchTypeFilter === 'all' ? "#0172A6" : "#E8E8E8",
                color: batchTypeFilter === 'all' ? "white" : "#6b7280",
                textTransform: "none",
                fontFamily: "Be Vietnam Pro",
                fontSize: "13px",
                "&:hover": {
                  backgroundColor: batchTypeFilter === 'all' ? "#0172A6" : "#d1d5db",
                },
              }}
              onClick={() => setBatchTypeFilter('all')}
            >
              All
            </Button>
            <Button
              size="small"
              sx={{
                backgroundColor: batchTypeFilter === '1:1' ? "#0172A6" : "#E8E8E8",
                color: batchTypeFilter === '1:1' ? "white" : "#6b7280",
                textTransform: "none",
                fontFamily: "Be Vietnam Pro",
                fontSize: "13px",
                "&:hover": {
                  backgroundColor: batchTypeFilter === '1:1' ? "#0172A6" : "#d1d5db",
                },
              }}
              onClick={() => setBatchTypeFilter('1:1')}
            >
              1:1
            </Button>
            <Button
              size="small"
              sx={{
                backgroundColor: batchTypeFilter === 'cohort' ? "#0172A6" : "#E8E8E8",
                color: batchTypeFilter === 'cohort' ? "white" : "#6b7280",
                textTransform: "none",
                fontFamily: "Be Vietnam Pro",
                fontSize: "13px",
                "&:hover": {
                  backgroundColor: batchTypeFilter === 'cohort' ? "#0172A6" : "#d1d5db",
                },
              }}
              onClick={() => setBatchTypeFilter('cohort')}
            >
              Cohorts
            </Button>
          </Box>

          <Box sx={{ width: '1px', height: '24px', backgroundColor: '#E8E8E8' }} />

          {/* Status filter */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              sx={{
                backgroundColor: filter === 'ongoing' ? "#0172A6" : "#E8E8E8",
                color: filter === 'ongoing' ? "white" : "#6b7280",
                textTransform: "capitalize",
                fontFamily: "Be Vietnam Pro",
                "&:hover": {
                  backgroundColor: filter === 'ongoing' ? "#0172A6" : "#E8E8E8",
                },
              }}
              onClick={() => setFilter(filter === 'ongoing' ? 'all' : 'ongoing')}
            >
              Ongoing
            </Button>
            <Button
              sx={{
                backgroundColor: filter === 'completed' ? "#0172A6" : "#E8E8E8",
                color: filter === 'completed' ? "white" : "#6b7280",
                textTransform: "capitalize",
                fontFamily: "Be Vietnam Pro",
                "&:hover": {
                  backgroundColor: filter === 'completed' ? "#0172A6" : "#E8E8E8",
                },
              }}
              onClick={() => setFilter(filter === 'completed' ? 'all' : 'completed')}
            >
              Completed
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {filteredData.length > 0 ? (
          filteredData.map((item, index) => {
            // Render BatchCard for batch items (both cohort and individual)
            if (item.type === 'batch') {
              return <BatchCard key={item.id || index} batch={item} onToggleComplete={handleToggleComplete} />;
            }
            // Render legacy MenteeCard for non-batch items
            return (
              <MenteeCard
                key={item.id || index}
                mentee={item}
                onToggleComplete={handleToggleComplete}
              />
            );
          })
        ) : (
          <Grid item xs={12}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100px',
              width: '100%'
            }}>
              <Typography>
                {filter === 'ongoing' ? 'No ongoing courses' :
                 filter === 'completed' ? 'No completed courses' : 'No data available'}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Create Cohort Modal */}
      <Dialog
        open={createCohortOpen}
        onClose={() => setCreateCohortOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "Be Vietnam Pro", fontWeight: 600 }}>
          Create New Cohort
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Cohort Name"
            fullWidth
            value={newCohortName}
            onChange={(e) => setNewCohortName(e.target.value)}
            placeholder="e.g., DS Batch 8, ML Cohort Q4"
            sx={{
              mt: 1,
              "& .MuiInputBase-root": {
                fontFamily: "Be Vietnam Pro",
              },
              "& .MuiInputLabel-root": {
                fontFamily: "Be Vietnam Pro",
              },
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !creatingCohort) {
                handleCreateCohort();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateCohortOpen(false)}
            sx={{
              textTransform: "none",
              fontFamily: "Be Vietnam Pro",
              color: "#6b7280",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCohort}
            variant="contained"
            disabled={creatingCohort || !newCohortName.trim()}
            sx={{
              backgroundColor: "#0172A6",
              textTransform: "none",
              fontFamily: "Be Vietnam Pro",
              "&:hover": {
                backgroundColor: "#015a84",
              },
            }}
          >
            {creatingCohort ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenteeCardList;
